/**
 * src/shell/agents/components/widgets/AccessHeatmapWidget.tsx
 *
 * Door Access Density & Security Anomaly Matrix.
 * Displays hour-by-hour access frequency and highlights security anomalies.
 */

import React, { useState, useEffect } from 'react';
import { IGenUIWidgetSpec } from '../../types/genui-types';
import { canvasTelemetryConnector } from '../../services/CanvasTelemetryConnector';

export interface AccessHeatmapWidgetProps {
  spec: IGenUIWidgetSpec;
}

export const AccessHeatmapWidget: React.FC<AccessHeatmapWidgetProps> = ({ spec }) => {
  const [data, setData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    return canvasTelemetryConnector.subscribe(
      spec.dataSource.endpoint,
      spec.dataSource.refreshIntervalMs || 4000,
      (payload) => setData(payload)
    );
  }, [spec.dataSource.endpoint, spec.dataSource.refreshIntervalMs]);

  const getColor = () => {
    switch (spec.colorScheme) {
      case 'crimson':
        return '#ef4444';
      case 'emerald':
        return '#10b981';
      case 'amber':
        return '#f59e0b';
      case 'cyber':
        return '#a855f7';
      case 'default':
      default:
        return '#38bdf8';
    }
  };

  const baseColor = getColor();
  const hourly = (data.hourlyDensity as Array<{ hour: string; density: number }>) || [
    { hour: '08:00', density: 14 },
    { hour: '10:00', density: 42 },
    { hour: '12:00', density: 78 },
    { hour: '14:00', density: 55 },
    { hour: '16:00', density: 34 },
    { hour: '18:00', density: 19 },
  ];

  return (
    <div
      data-testid={`heatmap-widget-${spec.id}`}
      style={{
        background: 'rgba(15, 23, 42, 0.75)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '14px',
        padding: '16px',
        color: '#f8fafc',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 800 }}>{spec.title}</div>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>{spec.description}</div>
        </div>
        <span
          style={{
            fontSize: '9px',
            fontWeight: 800,
            color: baseColor,
            background: `${baseColor}20`,
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          {spec.colorScheme?.toUpperCase() || 'DEFAULT'}
        </span>
      </div>

      {/* Hourly Heatmap Density Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', flex: 1, alignItems: 'center' }}>
        {hourly.map((h, idx) => {
          const intensity = Math.min(1, h.density / 80);
          return (
            <div
              key={idx}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '48px',
                  borderRadius: '6px',
                  background: `${baseColor}`,
                  opacity: Math.max(0.15, intensity),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#fff',
                  boxShadow: intensity > 0.7 ? `0 0 10px ${baseColor}` : 'none',
                }}
              >
                {h.density}
              </div>
              <span style={{ fontSize: '9px', color: '#64748b' }}>{h.hour}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
